// src/features/tickets/components/TicketCustomerAssignModal.tsx
//
// Fenster fuer die manuelle Kundenzuordnung eines Tickets.
// Waehlbar sind ein Kunde, eine Firmengruppe (Gesellschaft offen) oder der
// Pool. Bei einem Kunden kann zusaetzlich festgelegt werden, dass die
// Absenderadresse bzw. die ganze Domain kuenftig diesem Kunden zugeordnet wird
// (Regel in ticket_absender_regeln, angelegt ueber den TicketService).
//
// Oben stehen die Vorschlaege aus dem Zuordnungskontext (Kunden mit dieser
// Adresse, sonst mit dieser Domain, bzw. die Gesellschaften der Ticket-Gruppe).
// Das Fenster schliesst nur ueber X bzw. Abbrechen, nicht per Klick auf den
// Hintergrund (projektweites Muster).

import React, { useEffect, useMemo, useState } from 'react';
import { X, Loader, Search } from 'lucide-react';
import { customerService } from '../../../services/customer/CustomerService';
import { Customer } from '../../../shared/types';
import { ticketService } from '../services/TicketService';
import {
  TicketCustomerGroupRef,
  TicketKundenKontext,
  TicketKundenRegelArt,
  TicketKundenZuordnung
} from '../types';

interface TicketCustomerAssignModalProps {
  isOpen: boolean;
  currentCustomerId: string | null;
  currentGroupId: string | null;
  kontext: TicketKundenKontext | null;
  /** Vorauswahl, z. B. nach Klick auf einen Vorschlag in der Seitenleiste. */
  preselectedCustomerId?: string | null;
  saving: boolean;
  onClose: () => void;
  onSave: (zuordnung: TicketKundenZuordnung) => void;
}

type Auswahl =
  | { typ: 'kunde'; id: string }
  | { typ: 'gruppe'; id: string }
  | { typ: 'pool' };

const GRUND_LABELS: Record<string, string> = {
  adresse: 'Adresse hinterlegt',
  domain: 'gleiche Domain',
  gruppe: 'Gesellschaft der Gruppe'
};

export const TicketCustomerAssignModal: React.FC<TicketCustomerAssignModalProps> = ({
  isOpen,
  currentCustomerId,
  currentGroupId,
  kontext,
  preselectedCustomerId,
  saving,
  onClose,
  onSave
}) => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [groups, setGroups] = useState<TicketCustomerGroupRef[]>([]);
  const [loadingLists, setLoadingLists] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [auswahl, setAuswahl] = useState<Auswahl>({ typ: 'pool' });
  const [regel, setRegel] = useState<TicketKundenRegelArt>('keine');

  // Kunden und Gruppen beim Oeffnen laden
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    const load = async () => {
      setLoadingLists(true);
      setLoadError(false);
      try {
        const [customerData, groupData] = await Promise.all([
          customerService.getAllCustomers(),
          ticketService.listCustomerGroups()
        ]);
        if (cancelled) return;
        setCustomers(customerData);
        setGroups(groupData);
      } catch (error) {
        console.error('[TicketCustomerAssignModal] Load-Error:', error);
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoadingLists(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // Auswahl beim Oeffnen setzen: Vorauswahl, sonst aktueller Stand
  useEffect(() => {
    if (!isOpen) return;
    if (preselectedCustomerId) {
      setAuswahl({ typ: 'kunde', id: preselectedCustomerId });
    } else if (currentCustomerId) {
      setAuswahl({ typ: 'kunde', id: currentCustomerId });
    } else if (currentGroupId) {
      setAuswahl({ typ: 'gruppe', id: currentGroupId });
    } else {
      setAuswahl({ typ: 'pool' });
    }
    setRegel('keine');
    setSearchTerm('');
  }, [isOpen, preselectedCustomerId, currentCustomerId, currentGroupId]);

  // Regel nur bei Kunde; bei Wechsel auf Gruppe oder Pool zuruecksetzen
  useEffect(() => {
    if (auswahl.typ !== 'kunde') setRegel('keine');
  }, [auswahl]);

  const term = searchTerm.trim().toLowerCase();

  const vorschlaege = useMemo(() => {
    const list = kontext?.vorschlaege ?? [];
    if (!term) return list;
    return list.filter(v => v.company.toLowerCase().includes(term));
  }, [kontext, term]);

  const gefilterteGruppen = useMemo(() => {
    if (!term) return groups;
    return groups.filter(g => g.name.toLowerCase().includes(term));
  }, [groups, term]);

  const gefilterteKunden = useMemo(() => {
    if (!term) return customers;
    return customers.filter(c => {
      const companyMatch = c.company?.toLowerCase().includes(term) ?? false;
      const mandantMatch = c.mandantennummer?.toLowerCase().includes(term) ?? false;
      return companyMatch || mandantMatch;
    });
  }, [customers, term]);

  if (!isOpen) return null;

  const unveraendert =
    regel === 'keine' && (
      (auswahl.typ === 'kunde' && auswahl.id === currentCustomerId) ||
      (auswahl.typ === 'gruppe' && !currentCustomerId && auswahl.id === currentGroupId) ||
      (auswahl.typ === 'pool' && !currentCustomerId && !currentGroupId)
    );

  const handleSave = () => {
    onSave({
      customer_id: auswahl.typ === 'kunde' ? auswahl.id : null,
      customer_group_id: auswahl.typ === 'gruppe' ? auswahl.id : null,
      regel: auswahl.typ === 'kunde' ? regel : 'keine'
    });
  };

  const isSelected = (a: Auswahl): boolean => {
    if (a.typ !== auswahl.typ) return false;
    if (a.typ === 'pool') return true;
    return auswahl.typ !== 'pool' && a.id === auswahl.id;
  };

  const mehrereDomainKunden = (kontext?.vorschlaege ?? []).length > 1;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(17, 24, 39, 0.45)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '560px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'white',
        borderRadius: '10px',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)'
      }}>
        {/* Kopf */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          borderBottom: '1px solid #E5E7EB'
        }}>
          <div>
            <div style={{ fontSize: '16px', fontWeight: 600, color: '#111827' }}>Kunde zuordnen</div>
            {kontext?.absender && (
              <div style={{ fontSize: '13px', color: '#6B7280', marginTop: '2px', wordBreak: 'break-all' }}>
                Absender: {kontext.absender}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label="Schließen"
            style={{
              background: 'none',
              border: 'none',
              cursor: saving ? 'not-allowed' : 'pointer',
              color: '#6B7280',
              padding: '4px',
              display: 'inline-flex'
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Suche */}
        <div style={{ padding: '12px 20px 8px' }}>
          <div style={{ position: 'relative' }}>
            <Search
              size={15}
              style={{
                position: 'absolute',
                left: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#9CA3AF',
                pointerEvents: 'none'
              }}
            />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Kunde oder Firmengruppe suchen..."
              autoFocus
              style={{
                width: '100%',
                padding: '8px 12px 8px 32px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>

        {/* Auswahlliste */}
        <div style={{ flex: 1, minHeight: '160px', overflowY: 'auto', padding: '4px 20px 12px' }}>
          {loadingLists ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#6B7280', fontSize: '14px' }}>
              <Loader size={16} style={{ animation: 'spin 1s linear infinite', marginRight: '6px', verticalAlign: 'middle' }} />
              Lade Kunden...
            </div>
          ) : loadError ? (
            <div style={{ padding: '12px', color: '#B91C1C', fontSize: '14px' }}>
              Die Kundenliste konnte nicht geladen werden. Bitte das Fenster schließen und erneut öffnen.
            </div>
          ) : (
            <>
              <OptionRow
                label="Pool (kein Kunde)"
                selected={isSelected({ typ: 'pool' })}
                onSelect={() => setAuswahl({ typ: 'pool' })}
              />

              {vorschlaege.length > 0 && (
                <>
                  <SectionLabel>Vorschläge</SectionLabel>
                  {vorschlaege.map(v => (
                    <OptionRow
                      key={`v-${v.customer_id}`}
                      label={v.company}
                      hint={GRUND_LABELS[v.grund] ?? v.grund}
                      selected={isSelected({ typ: 'kunde', id: v.customer_id })}
                      onSelect={() => setAuswahl({ typ: 'kunde', id: v.customer_id })}
                    />
                  ))}
                </>
              )}

              {gefilterteGruppen.length > 0 && (
                <>
                  <SectionLabel>Firmengruppe, Gesellschaft offen</SectionLabel>
                  {gefilterteGruppen.map(g => (
                    <OptionRow
                      key={`g-${g.id}`}
                      label={g.name}
                      hint="Gruppe"
                      selected={isSelected({ typ: 'gruppe', id: g.id })}
                      onSelect={() => setAuswahl({ typ: 'gruppe', id: g.id })}
                    />
                  ))}
                </>
              )}

              <SectionLabel>Alle Kunden</SectionLabel>
              {gefilterteKunden.length === 0 ? (
                <div style={{ fontSize: '13px', color: '#9CA3AF', padding: '6px 10px' }}>
                  Keine Kunden gefunden.
                </div>
              ) : (
                gefilterteKunden.map(c => (
                  <OptionRow
                    key={`k-${c.id}`}
                    label={c.company}
                    hint={c.mandantennummer ? `Mandant ${c.mandantennummer}` : undefined}
                    selected={isSelected({ typ: 'kunde', id: c.id })}
                    onSelect={() => setAuswahl({ typ: 'kunde', id: c.id })}
                  />
                ))
              )}
            </>
          )}
        </div>

        {/* Regel fuer kuenftige Mails (nur bei Kunde) */}
        {auswahl.typ === 'kunde' && (
          <div style={{ padding: '12px 20px', borderTop: '1px solid #E5E7EB', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#6B7280' }}>Künftige Mails</div>
            <RegelOption
              label="Nur dieses Ticket"
              checked={regel === 'keine'}
              onChange={() => setRegel('keine')}
            />
            {kontext?.adresse_waehlbar && kontext.absender && (
              <RegelOption
                label={`Absenderadresse ${kontext.absender} künftig diesem Kunden zuordnen`}
                checked={regel === 'adresse'}
                onChange={() => setRegel('adresse')}
              />
            )}
            {kontext?.domain_waehlbar && kontext.domain && (
              <RegelOption
                label={`Ganze Domain @${kontext.domain} künftig diesem Kunden zuordnen`}
                checked={regel === 'domain'}
                onChange={() => setRegel('domain')}
              />
            )}
            {regel === 'domain' && mehrereDomainKunden && (
              <div style={{
                fontSize: '12px',
                color: '#92400E',
                backgroundColor: '#FFFBEB',
                border: '1px solid #FDE68A',
                borderRadius: '6px',
                padding: '8px 10px'
              }}>
                Diese Domain nutzen mehrere Kunden. Mails dieser Domain, deren Absender keinem Kunden eindeutig zugeordnet ist, landen dann immer bei diesem Kunden.
              </div>
            )}
          </div>
        )}

        {/* Fuss */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '8px',
          padding: '12px 20px',
          borderTop: '1px solid #E5E7EB'
        }}>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            style={{
              padding: '8px 14px',
              backgroundColor: 'white',
              color: '#374151',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '14px',
              cursor: saving ? 'not-allowed' : 'pointer'
            }}
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || unveraendert || loadingLists}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              backgroundColor: '#2563EB',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: saving || unveraendert || loadingLists ? 'not-allowed' : 'pointer',
              opacity: saving || unveraendert || loadingLists ? 0.6 : 1
            }}
          >
            {saving && <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />}
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// Hilfskomponenten
// ============================================

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{
    fontSize: '12px',
    fontWeight: 600,
    color: '#6B7280',
    margin: '12px 0 4px'
  }}>
    {children}
  </div>
);

interface OptionRowProps {
  label: string;
  hint?: string;
  selected: boolean;
  onSelect: () => void;
}

const OptionRow: React.FC<OptionRowProps> = ({ label, hint, selected, onSelect }) => (
  <button
    type="button"
    onClick={onSelect}
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '10px',
      width: '100%',
      textAlign: 'left',
      padding: '7px 10px',
      marginBottom: '2px',
      backgroundColor: selected ? '#EFF6FF' : 'white',
      border: `1px solid ${selected ? '#BFDBFE' : 'transparent'}`,
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '14px',
      color: '#111827'
    }}
  >
    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: selected ? 600 : 400 }}>
      {label}
    </span>
    {hint && (
      <span style={{ flexShrink: 0, fontSize: '12px', color: '#6B7280' }}>{hint}</span>
    )}
  </button>
);

interface RegelOptionProps {
  label: string;
  checked: boolean;
  onChange: () => void;
}

const RegelOption: React.FC<RegelOptionProps> = ({ label, checked, onChange }) => (
  <label style={{
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    fontSize: '13px',
    color: '#374151',
    cursor: 'pointer',
    wordBreak: 'break-word'
  }}>
    <input
      type="radio"
      checked={checked}
      onChange={onChange}
      style={{ marginTop: '2px', cursor: 'pointer' }}
    />
    {label}
  </label>
);
